// Constants for MW (TriloChat). Kept out of the page/components so the chat UI,
// the PDF export and the API layer all agree on the same values.

// ── Message polling ───────────────────────────────────────────────────────────

// A background Lambda processes each message asynchronously, so the answer is
// polled for rather than streamed.
export const CHAT_POLL_INTERVAL_MS = 2000;
export const CHAT_MAX_POLL_MS = 10 * 60 * 1000;

export const CHAT_MESSAGE_STATUS = {
  pending: "pending",
  idle: "idle",
  complete: "complete",
  error: "error",
};

export const CHAT_MESSAGE_ROLE = {
  user: "user",
  assistant: "assistant",
  error: "error",
};

// ── Drafts ────────────────────────────────────────────────────────────────────

// Also used as a BEM modifier suffix on .trilochat__draft-card in chat.scss.
export const DRAFT_STATUS = {
  pending: "pending",
  editing: "editing",
  accepted: "accepted",
};

// Draft accept/edit decisions are per session and live only in the browser.
export const DRAFT_STORAGE_KEY_PREFIX = "trilochat_drafts_";

export const draftStorageKey = sessionId =>
  `${DRAFT_STORAGE_KEY_PREFIX}${sessionId}`;

// ── Composer ──────────────────────────────────────────────────────────────────

// Mirrors the min-height/max-height of .trilochat__textarea in chat.scss - the
// textarea auto-grows between them, which only JS can measure.
export const CHAT_TEXTAREA_MIN_HEIGHT_PX = 44;
export const CHAT_TEXTAREA_MAX_HEIGHT_PX = 160;

// ── Sessions ──────────────────────────────────────────────────────────────────

export const DEFAULT_SESSION_TITLE = "New chat";
export const SESSION_TITLE_MAX_LENGTH = 60;

// ── Export ────────────────────────────────────────────────────────────────────

export const EXPORT_SLUG_FALLBACK = "MW";
export const EXPORT_SLUG_MAX_LENGTH = 50;
export const EXPORT_CHAT_FILENAME_SUFFIX = "_chat.pdf";
export const EXPORT_DRAFTS_FILENAME_SUFFIX = "_accepted_text.pdf";

// Revoking the object URL in the same tick as the click can cancel the download
// in some browsers, so the blob is held on to for a while first.
export const EXPORT_BLOB_REVOKE_DELAY_MS = 10000;

// ── Inline document references ────────────────────────────────────────────────

// Assistant replies embed `[DOC:<content id>]` markers that are swapped for an
// inline document panel. Kept as a source string rather than a RegExp because
// each render needs its own `lastIndex`.
export const DOC_REFERENCE_PATTERN = "\\[DOC:([a-f0-9]+)\\]";

// ── Copy ──────────────────────────────────────────────────────────────────────

export const CHAT_MESSAGES = {
  genericError: "Something went wrong. Please try again.",
  timeoutError: "The request timed out. Please try again.",
  docPanelError: "Could not load document content.",
  docPanelLoading: "Loading document…",
  noSessions: "No conversations yet",
  composerPlaceholder: "Ask anything about your studies…",
  composerHint: "Enter to send · Shift+Enter for new line",
  noStudyContext: "No study context",
};

// ── Tool call labels ──────────────────────────────────────────────────────────

// Human-readable label for each backend tool, shown as a pill while MW works.
export const TOOL_LABELS = {
  find_study: "Finding study",
  list_studies: "Listing studies",
  get_study_details: "Fetching study details",
  get_request_status: "Checking study status",
  list_sections: "Checking sections",
  explain_section_output: "Explaining section output",
  explain_issues: "Checking issues",
  show_section: "Reading section",
  read_document: "Reading document",
  compare_wizard_answers: "Comparing study answers",
  list_documents: "Listing documents",
  list_protocol_deviations: "Checking protocol deviations",
  assess_deviation: "Assessing deviation",
  record_deviation_decision: "Recording decision",
  get_deviation_decisions: "Loading decisions",
  list_demography_differences: "Checking demographics",
  assess_demography_difference: "Assessing demographic difference",
  record_demography_decision: "Recording decision",
  get_demography_decisions: "Loading decisions",
  review_secondary_efficacy: "Reviewing efficacy data",
  review_adverse_events: "Reviewing adverse events",
  search_tfl_tables: "Searching tables",
  find_patient_narrative: "Finding Patient Narrative",
  list_patient_narratives: "Listing Patient Narratives",
  get_patient_narrative_wizard: "Checking narrative set configuration",
  list_patient_narrative_documents: "Listing narratives",
  read_patient_narrative_document: "Reading narrative",
  list_patient_narrative_source_documents: "Listing source documents",
  read_patient_narrative_source_document: "Reading source document",
};

// ── PDF export: Unicode super/subscript folding ───────────────────────────────

// Inverse maps: Unicode super/subscript → ASCII, used in the PDF export's
// inline-run pre-processing. Neither PDF font covers the whole range - Public
// Sans has no subscript glyphs, and Helvetica is an 8-bit Type 1 font whose
// chars outside WinAnsiEncoding are mangled by PDFKit - so they are rendered as
// small ASCII instead.
export const UNICODE_SUPERSCRIPT_TO_ASCII = {
  "⁰": "0",
  "¹": "1",
  "²": "2",
  "³": "3",
  "⁴": "4",
  "⁵": "5",
  "⁶": "6",
  "⁷": "7",
  "⁸": "8",
  "⁹": "9",
  "⁺": "+",
  "⁻": "-",
  "⁼": "=",
  "⁽": "(",
  "⁾": ")",
  ⁿ: "n",
  ⁱ: "i",
};

export const UNICODE_SUBSCRIPT_TO_ASCII = {
  "₀": "0",
  "₁": "1",
  "₂": "2",
  "₃": "3",
  "₄": "4",
  "₅": "5",
  "₆": "6",
  "₇": "7",
  "₈": "8",
  "₉": "9",
  "₊": "+",
  "₋": "-",
  "₌": "=",
  "₍": "(",
  "₎": ")",
};

export const UNICODE_SUPERSCRIPT_REGEX = /[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻⁼⁽⁾ⁿⁱ]+/gu;
export const UNICODE_SUBSCRIPT_REGEX = /[₀₁₂₃₄₅₆₇₈₉₊₋₌₍₎]+/gu;
